import { useState } from "react";
import {
  Platform,
  Pressable,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type CostsCardTabOption<T extends string> = {
  value: T;
  label: string;
  icon: LucideIcon;
};

type Props<T extends string> = {
  active: T;
  onChange: (value: T) => void;
  options: CostsCardTabOption<T>[];
  style?: StyleProp<ViewStyle>;
};

/** The raised tab treatment used by the Costs money card. */
export default function CostsCardTabs<T extends string>({
  active,
  onChange,
  options,
  style,
}: Props<T>) {
  const capturePress = usePressCapture("CostsCardTabs");
  const [hoveredTab, setHoveredTab] = useState<T | null>(null);

  return (
    <View
      style={[
        {
          alignItems: "center",
          marginBottom: -10,
          zIndex: 30,
          elevation: 30,
        },
        style,
      ]}
    >
      <View style={{ width: "90%", flexDirection: "row", gap: 8 }}>
        {options.map((option) => {
          const selected = option.value === active;
          const hovered = hoveredTab === option.value;
          const OptionIcon = option.icon;
          const optionColor = selected
            ? EMBER.colors.text
            : EMBER.colors.textLight;

          return (
            <Pressable
              key={option.value}
              onPress={() => {
                capturePress(pressLabel(option.label), "costs_card_tab");
                onChange(option.value);
              }}
              onHoverIn={() => setHoveredTab(option.value)}
              onHoverOut={() =>
                setHoveredTab((current) =>
                  current === option.value ? null : current,
                )
              }
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              aria-selected={selected}
              style={({ pressed }) => ({
                flex: 1,
                minHeight: 48,
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 5,
                paddingHorizontal: 7,
                paddingTop: 9,
                paddingBottom: 14,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                borderBottomLeftRadius: 10,
                borderBottomRightRadius: 10,
                borderWidth: 1,
                borderBottomWidth: 0,
                borderColor: selected
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(255, 255, 255, 0.34)",
                backgroundColor: selected
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(255, 255, 255, 0.12)",
                shadowColor: "#000",
                shadowOpacity: selected ? 0.08 : hovered ? 0.055 : 0.025,
                shadowRadius: selected ? 32 : hovered ? 22 : 10,
                shadowOffset: { width: 0, height: hovered ? -6 : -2 },
                transform: [
                  {
                    translateY: pressed ? -2 : hovered ? -6 : 0,
                  },
                ],
                ...(Platform.OS === "web"
                  ? {
                      transition:
                        "transform 180ms cubic-bezier(0.2, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.2, 0, 0.2, 1)",
                    }
                  : null),
              })}
            >
              <OptionIcon
                size={13}
                color={optionColor}
                strokeWidth={selected ? 2.2 : 1.8}
              />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.78}
                style={{
                  flexShrink: 1,
                  fontSize: 11,
                  fontWeight: selected ? "700" : "600",
                  color: optionColor,
                }}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
